import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { Brand } from '../../components/Brand.tsx';
import { useTranslate } from '../../i18n/index.tsx';
import { login, requestLoginLink } from '../../services/auth.service.ts';
import { useAuthStore } from '../../stores/auth.store.ts';
import { ApiRequestError } from '../../services/api.ts';

export const SignInPage = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const restore = useAuthStore((state) => state.restore);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      await restore();
      // Let the landing route decide: a leader cannot read the dashboard.
      navigate('/', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError && caught.status === 401
          ? t('auth.invalidCredentials')
          : t('errors.generic'),
      );
    } finally {
      setBusy(false);
    }
  };

  const sendLink = async () => {
    setBusy(true);
    setError(null);
    // The response is identical for known and unknown addresses by design.
    await requestLoginLink(email).catch(() => undefined);
    setNotice(t('auth.linkSent'));
    setBusy(false);
  };

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col px-5">
      <Brand />
      <main className="flex flex-1 flex-col justify-center py-10">
        <h1 className="text-2xl font-extrabold tracking-tight">{t('auth.signIn')}</h1>

        <form onSubmit={submit} className="mt-6 flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="font-mono text-[10px] tracking-[0.13em] text-faint uppercase">
              {t('auth.email')}
            </span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="rounded-xl border border-line bg-paper px-4 py-3 text-[15px] outline-none focus:border-teal"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="font-mono text-[10px] tracking-[0.13em] text-faint uppercase">
              {t('auth.password')}
            </span>
            {/* The reveal toggle is a button, not a checkbox: it performs an
                action rather than recording a preference, and it announces its
                own state so a screen reader is not left guessing. */}
            <span className="relative flex items-center">
              <input
                type={revealed ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full rounded-xl border border-line bg-paper py-3 pr-12 pl-4 text-[15px] outline-none focus:border-teal"
              />
              <button
                type="button"
                onClick={() => setRevealed((shown) => !shown)}
                aria-pressed={revealed}
                aria-label={revealed ? t('auth.hidePassword') : t('auth.showPassword')}
                title={revealed ? t('auth.hidePassword') : t('auth.showPassword')}
                className="absolute right-1.5 grid size-9 cursor-pointer place-items-center rounded-lg border-none bg-transparent text-soft transition hover:text-teal"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="size-[18px]"
                  aria-hidden
                >
                  <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
                  <circle cx="12" cy="12" r="2.6" />
                  {revealed && <path d="m4 20 16-16" />}
                </svg>
              </button>
            </span>
          </label>

          {error && <p className="text-sm text-red-700">{error}</p>}
          {notice && <p className="text-sm text-teal-deep">{notice}</p>}

          <button
            type="submit"
            disabled={busy || !email || !password}
            className="rounded-xl bg-teal px-4 py-3.5 text-[15px] font-bold text-white shadow-action transition hover:bg-teal-deep disabled:bg-[#CDCAC2] disabled:shadow-none"
          >
            {t('auth.signIn')}
          </button>

          <button
            type="button"
            onClick={sendLink}
            disabled={busy || !email}
            className="font-mono text-[12px] tracking-[0.06em] text-soft uppercase underline underline-offset-2 disabled:opacity-40"
          >
            {t('auth.sendLink')}
          </button>
        </form>
      </main>
    </div>
  );
};
